import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, WandSparkles } from "lucide-react";

const TONES = ["Professional","Witty","Bold","Storytelling","Viral","Luxury","Playful"];
const LENGTHS = ["Short & Punchy","Medium","Long-Form / Story"];

export default function AICaption({ open, onOpenChange, topic, platform, clientName, onApply }) {
  const [model, setModel] = useState("claude");
  const [tone, setTone] = useState("Professional");
  const [length, setLength] = useState("Medium");
  const [industry, setIndustry] = useState("");
  const [topicLocal, setTopicLocal] = useState(topic || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!topicLocal.trim()) { toast.error("Add a topic first"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ai/caption", {
        model, topic: topicLocal, platform: platform || "Instagram", tone, length, industry
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally { setBusy(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-caption-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <WandSparkles className="w-5 h-5 text-orange-400"/>
            AI Caption Studio
            {clientName && <span className="text-xs font-mono text-neutral-500 ml-2">· {clientName}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* Model switcher */}
        <div className="grid grid-cols-2 gap-2 p-1 rounded-lg border border-[#E4E7EC] bg-neutral-50">
          <button onClick={()=>setModel("claude")} data-testid="ai-model-claude" className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${model==="claude"?"bg-orange-100 border border-orange-300 text-orange-700 shadow-sm":"text-neutral-600 hover:text-neutral-900"}`}>
            Claude Sonnet 5
          </button>
          <button onClick={()=>setModel("gpt")} data-testid="ai-model-gpt" className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${model==="gpt"?"bg-emerald-100 border border-emerald-300 text-emerald-700 shadow-sm":"text-neutral-600 hover:text-neutral-900"}`}>
            GPT 5.6
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Topic</Label>
            <Input value={topicLocal} onChange={e=>setTopicLocal(e.target.value)} data-testid="ai-topic" className="bg-[#FFFFFF] border-[#E4E7EC]" placeholder="e.g. New product launch this weekend"/>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="ai-tone"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{TONES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Length</Label>
            <Select value={length} onValueChange={setLength}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="ai-length"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{LENGTHS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Industry / Context</Label>
            <Input value={industry} onChange={e=>setIndustry(e.target.value)} data-testid="ai-industry" placeholder="e.g. Luxury Apparel" className="bg-[#FFFFFF] border-[#E4E7EC]"/>
          </div>
        </div>

        <Button onClick={generate} disabled={busy} data-testid="ai-generate-btn" className="w-full bg-gradient-to-br from-orange-500 to-red-600 text-white hover:opacity-95">
          <Sparkles className="w-4 h-4 mr-2"/> {busy ? "Crafting..." : "Generate Caption"}
        </Button>

        {result && (
          <div className="space-y-3 mt-2" data-testid="ai-result">
            <div className="panel p-4 bg-[#FFFFFF]">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-mono uppercase tracking-wider text-orange-400">Caption · {result.model}</div>
                <button onClick={()=>copy(result.caption)} className="text-neutral-600 hover:text-neutral-800"><Copy className="w-3.5 h-3.5"/></button>
              </div>
              <div className="text-sm whitespace-pre-wrap">{result.caption || result.raw}</div>
            </div>
            {result.cta && (
              <div className="panel p-3 bg-[#FFFFFF]">
                <div className="text-[10px] font-mono uppercase text-orange-400">CTA</div>
                <div className="text-sm">{result.cta}</div>
              </div>
            )}
            {result.hashtags && (
              <div className="panel p-3 bg-[#FFFFFF]">
                <div className="text-[10px] font-mono uppercase text-orange-400">Hashtags</div>
                <div className="text-sm font-mono text-neutral-700 break-words">{result.hashtags}</div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="ai-close">Close</Button>
          {result && <Button onClick={()=>onApply?.(result)} data-testid="ai-apply" className="bg-orange-500 hover:bg-orange-600 text-white">Apply to Content</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
